import { z } from 'zod'
import {
  localizedErrorMessageTemplates,
  LocalizedErrorDto,
} from './localizedErrorMessages'

export const UTCInstant = z
  .string()
  .datetime()
  .refine(
    (value) => Number.isFinite(Date.parse(value)),
    'Expected a UTC timestamp',
  )
export const MessageParameters = z.record(
  z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/),
  z.union([z.string().max(4096), z.number().finite(), z.boolean(), z.null()]),
)

type MessagePart =
  | { text: string }
  | { parameter: string; format: 'DATETIME_SHORT' }

/** Parse once so literal braces and inserted values cannot become new tokens. */
export function parseMessageTokens(message: string): MessagePart[] {
  const parts: MessagePart[] = []
  let text = ''
  for (let index = 0; index < message.length; ) {
    const character = message[index]!
    if (
      (character === '{' || character === '}') &&
      message[index + 1] === character
    ) {
      text += character
      index += 2
    } else if (character === '{') {
      if (text) parts.push({ text })
      text = ''
      const end = message.indexOf('}', index + 1)
      const token = message.slice(index + 1, end)
      const match = /^([A-Za-z][A-Za-z0-9_]*):DATETIME_SHORT$/.exec(token)
      if (end < 0 || !match) throw new Error('Unsupported message token')
      parts.push({ parameter: match[1]!, format: 'DATETIME_SHORT' })
      index = end + 1
    } else if (character === '}') {
      throw new Error('Unescaped message brace')
    } else {
      text += character
      index += 1
    }
  }
  if (text) parts.push({ text })
  return parts
}

const MessageFields = z
  .object({
    key: z.string().min(1).max(160),
    version: z.number().int().positive(),
    params: MessageParameters,
    messages: z.record(z.string().min(1).max(64), z.string().min(1).max(4096)),
  })
  .strict()

// Receivers preserve unfamiliar formats so the renderer can provide its generic fallback.
export const LocalizedMessageWireDto = MessageFields

export const LocalizedMessageDto = MessageFields.superRefine(
  (message, context) => {
    if (!message.messages.en) {
      context.addIssue({
        code: 'custom',
        path: ['messages', 'en'],
        message: 'English fallback is required',
      })
    }
    for (const [locale, text] of Object.entries(message.messages)) {
      try {
        for (const part of parseMessageTokens(text)) {
          if (
            'parameter' in part &&
            !UTCInstant.safeParse(message.params[part.parameter]).success
          ) {
            throw new Error('Missing or invalid UTC timestamp parameter')
          }
        }
      } catch (error) {
        context.addIssue({
          code: 'custom',
          path: ['messages', locale],
          message: (error as Error).message,
        })
      }
    }
  },
)
export type LocalizedMessage = z.infer<typeof LocalizedMessageDto>

export function escapeMessageText(text: string): string {
  return text.replaceAll('{', '{{').replaceAll('}', '}}')
}

export function normalizeMessageLocale(locale: string): string {
  return locale.replaceAll('_', '-').replace(/^jp(?=-|$)/i, 'ja')
}

export function selectMessageText(
  message: LocalizedMessage,
  locale: string,
): string {
  const normalized = normalizeMessageLocale(locale).toLowerCase()
  const translations = new Map(
    Object.entries(message.messages).map(([key, text]) => [
      normalizeMessageLocale(key).toLowerCase(),
      text,
    ]),
  )
  return (
    translations.get(normalized) ??
    translations.get(normalized.split('-')[0]!) ??
    message.messages.en!
  )
}

/** Unknown keys work because only the embedded wording and shared formats are used. */
export function renderLocalizedMessage(
  input: unknown,
  options: { locale?: string; timeZone?: string; fallback?: string } = {},
): string {
  const locale = normalizeMessageLocale(
    options.locale ?? Intl.DateTimeFormat().resolvedOptions().locale,
  )
  const genericMessages: Record<string, string> = {
    en: 'Status unavailable',
    ja: '状況を表示できません',
    fr: 'État indisponible',
    it: 'Stato non disponibile',
  }
  const fallback =
    options.fallback ??
    genericMessages[locale.toLowerCase().split('-')[0]!] ??
    'Status unavailable'
  try {
    const message = LocalizedMessageDto.parse(input)
    const timeZone =
      options.timeZone ??
      Intl.DateTimeFormat().resolvedOptions().timeZone ??
      'UTC'
    return parseMessageTokens(selectMessageText(message, locale))
      .map((part) => {
        if ('text' in part) return part.text
        return new Intl.DateTimeFormat(locale, {
          dateStyle: 'short',
          timeStyle: 'short',
          timeZone,
        }).format(new Date(message.params[part.parameter] as string))
      })
      .join('')
  } catch {
    return fallback
  }
}

export function adaptLocalizedError(
  input: z.infer<typeof LocalizedErrorDto>,
): LocalizedMessage {
  const error = LocalizedErrorDto.parse(input)
  const template = localizedErrorMessageTemplates[error.error_key]
  if (!template) throw new Error('Unknown legacy error template')
  const messages = Object.fromEntries(
    Object.entries(template.messages)
      .map(([locale, text]) => [
        normalizeMessageLocale(locale),
        text.replace(/\{([A-Za-z][A-Za-z0-9_]*)\}/g, (_, key: string) => {
          if (error.params[key] === undefined)
            throw new Error(`Missing legacy message parameter: ${key}`)
          return String(error.params[key])
        }),
      ])
      .map(([locale, text]) => [locale, escapeMessageText(text!)]),
  )
  return LocalizedMessageDto.parse({
    key: error.error_key,
    version: 1,
    params: error.params,
    messages,
  })
}

/** Producers explicitly cover their target locales; consumers still support language fallback. */
export const MonocleJobMessageLocales = ['en', 'ja', 'fr', 'it'] as const
export function validateMessageCoverage(
  input: unknown,
  locales: readonly string[],
): LocalizedMessage {
  const message = LocalizedMessageDto.parse(input)
  const supplied = new Set(
    Object.keys(message.messages).map((locale) =>
      normalizeMessageLocale(locale).toLowerCase(),
    ),
  )
  for (const locale of locales) {
    const target = normalizeMessageLocale(locale).toLowerCase()
    if (!supplied.has(target) && !supplied.has(target.split('-')[0]!))
      throw new Error(`Missing message locale: ${locale}`)
  }
  return message
}
